import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, Clock, ExternalLink, X, ChevronRight, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProofImage = ({ url, onReplace }) => {
  const [failed, setFailed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onReplace) return;
    setUploading(true);
    try {
      await onReplace(file);
      setFailed(false);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center p-4" onClick={() => setShowLightbox(false)}>
          <button onClick={() => setShowLightbox(false)} className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2 hover:bg-black/60">
            <X className="w-5 h-5" />
          </button>
          <img src={url} alt="Bukti transfer" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
      <p className="text-xs font-semibold text-isk-muted mb-2">Bukti Transfer</p>
      {failed ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center space-y-2">
          <p className="text-sm text-red-500 font-medium">Foto tidak tersedia</p>
          <p className="text-xs text-red-400">File asli tidak ditemukan di server</p>
          {onReplace && (
            <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-isk-teal text-white hover:bg-isk-dark'}`}>
              {uploading ? 'Mengupload...' : '↑ Ganti Foto'}
              <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={uploading} />
            </label>
          )}
        </div>
      ) : (
        <>
          <div className="relative group">
            <div className="rounded-xl overflow-hidden border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer"
              onClick={() => setShowLightbox(true)}>
              <img
                src={url}
                alt="Bukti transfer"
                className="w-full max-h-64 object-contain bg-gray-50"
                onError={() => setFailed(true)}
              />
            </div>
            {onReplace && (
              <label className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-black/60 text-white cursor-pointer hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity">
                {uploading ? 'Mengupload...' : '↑ Ganti'}
                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={uploading} />
              </label>
            )}
          </div>
          <button onClick={() => setShowLightbox(true)}
            className="mt-1 inline-flex items-center gap-1 text-xs text-isk-teal hover:underline">
            <ExternalLink className="w-3 h-3" /> Buka gambar penuh
          </button>
        </>
      )}
    </div>
  );
};

const PaymentVerification = ({ userRole }) => userRole === 'superadmin' ? <SuperadminPaymentVerification /> : null;

const SuperadminPaymentVerification = () => {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selected, setSelected] = useState(null);
  const [receivedDate, setReceivedDate] = useState('');
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [editingPaidAt, setEditingPaidAt] = useState(false);
  const [newPaidAt, setNewPaidAt] = useState('');
  const [proofImageUrl, setProofImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [proofImageFailed, setProofImageFailed] = useState(false);

  useEffect(() => { fetchProofs(); }, []);

  const fetchProofImage = (proofId) => {
    setLoadingImage(true);
    setProofImageFailed(false);
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    axios.get(`${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(proofId)}/image`, config)
      .then(r => { setProofImageUrl(r.data.proof_image_url || null); setProofImageFailed(!r.data.proof_image_url); })
      .catch(() => { setProofImageUrl(null); setProofImageFailed(true); })
      .finally(() => setLoadingImage(false));
  };

  useEffect(() => {
    setReceivedDate(selected?.submitted_at?.slice(0, 10) || '');
    setEditingPaidAt(false);
    setNewPaidAt('');
    setProofImageUrl(null);
    setProofImageFailed(false);
    if (!selected) return;
    fetchProofImage(selected.id);
  }, [selected]);

  const fetchProofs = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      const res = await axios.get(`${BACKEND_URL}/api/admin/payment-proofs`, config);
      setProofs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleReplaceImage = async (proofId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    await axios.put(`${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(proofId)}/image`, fd, config);
    fetchProofImage(proofId);
    setSuccess('Foto berhasil diganti!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleVerify = async (proofId, action, date) => {
    if (action === 'approve' && !date) { setError('Masukkan tanggal uang diterima terlebih dahulu'); return; }
    if (action === 'reject' && !window.confirm('Tolak pembayaran ini?')) return;
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      await axios.put(
        `${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(proofId)}/verify?action=${action}`,
        { received_date: date || null },
        config
      );
      setSuccess(action === 'approve' ? 'Pembayaran disetujui!' : 'Pembayaran ditolak.');
      setSelected(null);
      fetchProofs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal memproses'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Hapus bukti pembayaran ini secara permanen?')) return;
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      await axios.delete(`${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(selected.id)}`, config);
      setSuccess('Bukti pembayaran berhasil dihapus');
      setSelected(null);
      fetchProofs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menghapus'); }
  };

  const handleUpdatePaidAt = async () => {
    if (!newPaidAt) return;
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      await axios.put(
        `${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(selected.id)}/paid-at`,
        { paid_at: newPaidAt },
        config
      );
      setSelected(prev => ({ ...prev, bill: { ...prev.bill, paid_at: newPaidAt } }));
      setEditingPaidAt(false);
      setNewPaidAt('');
      setSuccess('Tanggal berhasil diperbarui!');
      setTimeout(() => setSuccess(''), 3000);
      fetchProofs();
    } catch (e) { setError(e.response?.data?.detail || 'Gagal memperbarui tanggal'); }
  };

  const fmt = (n) => n ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n) : '-';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const getImgUrl = (url) => !url ? '' : (url.startsWith('http') || url.startsWith('data:')) ? url : `${BACKEND_URL}${url}`;

  const monthNameToNumber = { Januari:'01',Februari:'02',Maret:'03',April:'04',Mei:'05',Juni:'06',Juli:'07',Agustus:'08',September:'09',Oktober:'10',November:'11',Desember:'12' };
  const monthOptions = Object.entries(monthNameToNumber).map(([l,v]) => ({ label: l, value: v }));
  const getBillMonthKey = (p) => {
    const period = p.bill?.period;
    if (!period) return '';
    const parts = period.split(' ');
    if (parts.length === 2) { const [m,y] = parts; return `${y}-${monthNameToNumber[m]||'01'}`; }
    return p.submitted_at?.slice(0,7) || '';
  };
  const fmtMonth = (key) => { if (!key) return key; const [y,m] = key.split('-'); const l = monthOptions.find(o=>o.value===m)?.label||m; return `${l} ${y}`; };

  const StatusBadge = ({ s }) => ({
    pending_verification: <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Menunggu</span>,
    verified: <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Disetujui</span>,
    rejected: <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Ditolak</span>,
  }[s] || null);

  const propertyOptions = [...new Set(proofs.map(p => p.bill?.property_name).filter(Boolean))].sort();
  const availableMonths = [...new Set(proofs.map(p => getBillMonthKey(p)).filter(Boolean))].sort().reverse();

  const filtered = proofs.filter(p => {
    if (filterProperty !== 'all' && p.bill?.property_name !== filterProperty) return false;
    if (filterMonth !== 'all' && getBillMonthKey(p) !== filterMonth) return false;
    if (filterStatus !== 'all') {
      if (filterStatus === 'pending' && p.status !== 'pending_verification') return false;
      if (filterStatus === 'verified' && p.status !== 'verified') return false;
      if (filterStatus === 'rejected' && p.status !== 'rejected') return false;
    }
    return true;
  });

  const counts = {
    all: proofs.length,
    pending: proofs.filter(p => p.status === 'pending_verification').length,
    verified: proofs.filter(p => p.status === 'verified').length,
    rejected: proofs.filter(p => p.status === 'rejected').length,
  };

  if (loading) return <div className="text-center py-12"><div className="w-10 h-10 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-3"/><p className="text-isk-muted">Memuat data...</p></div>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-isk-text">Verifikasi Pembayaran</h2>
        <p className="text-sm text-isk-muted mt-1">Verifikasi bukti pembayaran dari tenant atau admin</p>
      </div>

      {success && <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20 text-isk-teal text-sm">{success}</div>}
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="flex gap-1 bg-white/40 rounded-xl p-1">
          {[['all','Semua'],['pending','Menunggu'],['verified','Disetujui'],['rejected','Ditolak']].map(([k,l]) => (
            <button key={k} onClick={() => setFilterStatus(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus===k ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
              {l} ({counts[k] ?? 0})
            </button>
          ))}
        </div>
        <Select value={filterProperty} onValueChange={setFilterProperty}>
          <SelectTrigger className="w-40 rounded-full text-xs h-8"><SelectValue placeholder="Semua Kost" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kost</SelectItem>
            {propertyOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="w-36 rounded-full text-xs h-8"><SelectValue placeholder="Semua Bulan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Bulan</SelectItem>
            {availableMonths.map(m => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="glass-card rounded-2xl overflow-hidden border border-white/40">
        <div className="hidden sm:grid grid-cols-[2fr_1fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_1fr_auto] gap-2 px-4 py-2.5 bg-white/40 border-b border-white/40 text-xs font-semibold text-isk-muted">
          <span>Tenant</span><span>Kamar</span><span>Properti</span><span>Periode</span><span>Nominal</span><span>Tgl Transfer</span><span>Tgl Tercatat</span><span>Status</span><span></span>
        </div>
        {filtered.length === 0 && <div className="py-12 text-center text-isk-muted text-sm">Tidak ada data</div>}
        <div className="divide-y divide-white/20">
          {filtered.map((p, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_1fr_auto] gap-2 px-4 py-3 items-center hover:bg-white/30 transition-colors">
              <div>
                <p className="font-semibold text-isk-text text-sm">{p.bill?.tenant_name || p.tenant_phone}</p>
                <p className="text-xs text-isk-muted sm:hidden">{p.bill?.property_name} · Kamar {p.bill?.room_number} · {fmtDate(p.submitted_at)}</p>
              </div>
              <p className="hidden sm:block text-sm text-isk-text">{p.bill?.room_number || '-'}</p>
              <p className="hidden sm:block text-xs text-isk-muted truncate">{p.bill?.property_name || '-'}</p>
              <p className="hidden sm:block text-xs text-isk-text">{p.bill?.period || '-'}</p>
              <p className="hidden sm:block text-sm font-semibold text-isk-teal">{fmt(p.bill?.total_amount)}</p>
              <p className="hidden sm:block text-xs text-isk-text">{fmtDate(p.submitted_at)}</p>
              <p className="hidden sm:block text-xs text-isk-text">
                {p.bill?.paid_at ? fmtDate(p.bill.paid_at) : <span className="text-isk-muted">-</span>}
              </p>
              <div className="hidden sm:block"><StatusBadge s={p.status} /></div>
              <Button size="sm" onClick={() => setSelected(p)}
                className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-7 px-3 transition-colors">
                Detail <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Detail Popup */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md flex flex-col max-h-[85vh]">
            <div className="flex justify-between items-center p-6 pb-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-isk-text">{selected.bill?.tenant_name || selected.tenant_phone}</h3>
                <p className="text-xs text-isk-muted mt-0.5">{selected.bill?.property_name} · Kamar {selected.bill?.room_number} · {selected.bill?.period}</p>
              </div>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5" /></button>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-3">
              {/* Info tagihan */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ['Periode', selected.bill?.period||'-'],
                  ['Nominal', fmt(selected.bill?.total_amount)],
                  ['Tanggal Pembayaran', fmtDate(selected.submitted_at)],
                  ['Transfer Tercatat', selected.bill?.paid_at ? fmtDate(selected.bill.paid_at) : null],
                  ['Status', null],
                ].map(([l, v], idx) => (
                  idx === 4 ? (
                    <div key={l} className="p-2 rounded-lg bg-gray-50">
                      <p className="text-isk-muted">{l}</p>
                      <div className="mt-0.5"><StatusBadge s={selected.status} /></div>
                    </div>
                  ) : idx === 3 && !v ? null : (
                    <div key={l} className="p-2 rounded-lg bg-gray-50">
                      <p className="text-isk-muted">{l}</p>
                      <p className="font-semibold text-isk-text">{v || '-'}</p>
                    </div>
                  )
                ))}
              </div>

              {selected.notes && (
                <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs">
                  <p className="font-medium text-isk-text mb-1">Catatan</p>
                  <p className="text-isk-muted">{selected.notes}</p>
                </div>
              )}

              {/* Bukti transfer */}
              {loadingImage ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-center">
                  <div className="w-6 h-6 border-2 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-xs text-isk-muted">Memuat gambar...</p>
                </div>
              ) : proofImageUrl ? (
                <ProofImage
                  key={proofImageUrl}
                  url={getImgUrl(proofImageUrl)}
                  onReplace={(file) => handleReplaceImage(selected.id, file)}
                />
              ) : proofImageFailed ? (
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-center space-y-2">
                  <p className="text-sm text-orange-600 font-medium">Gagal memuat gambar</p>
                  <div className="flex gap-2 justify-center">
                    <button onClick={() => fetchProofImage(selected.id)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-isk-teal text-white hover:bg-isk-dark">
                      Coba Lagi
                    </button>
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200">
                      ↑ Ganti Foto
                      <input type="file" accept="image/*" className="hidden"
                        onChange={async e => { const f = e.target.files?.[0]; if (f) await handleReplaceImage(selected.id, f); }} />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center space-y-2">
                  <p className="text-sm text-red-500 font-medium">Tidak ada bukti foto</p>
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-isk-teal text-white cursor-pointer hover:bg-isk-dark">
                    ↑ Upload Foto Bukti
                    <input type="file" accept="image/*" className="hidden"
                      onChange={async e => { const f = e.target.files?.[0]; if (f) await handleReplaceImage(selected.id, f); }} />
                  </label>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="p-6 pt-4 border-t border-gray-100 space-y-3">
              {selected.status === 'pending_verification' ? (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-isk-text mb-1">
                      Tanggal Uang Diterima <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={receivedDate}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={e => { setReceivedDate(e.target.value); setError(''); }}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-isk-teal"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleVerify(selected.id, 'reject', null)}
                      variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-full">
                      <XCircle className="w-4 h-4 mr-1" /> Tolak
                    </Button>
                    <Button
                      onClick={() => handleVerify(selected.id, 'approve', receivedDate)}
                      disabled={!receivedDate}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
                      <CheckCircle className="w-4 h-4 mr-1" /> Setujui
                    </Button>
                  </div>
                </>
              ) : selected.status === 'verified' ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-600 text-center">Pembayaran telah disetujui</p>
                  {editingPaidAt ? (
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-isk-text">Ubah Tanggal Tercatat</label>
                      <input
                        type="date"
                        value={newPaidAt}
                        onChange={e => setNewPaidAt(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-isk-teal"
                      />
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => { setEditingPaidAt(false); setNewPaidAt(''); }}
                          className="flex-1 rounded-full text-xs">Batal</Button>
                        <Button onClick={handleUpdatePaidAt} disabled={!newPaidAt}
                          className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full text-xs disabled:opacity-50">Simpan</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-xs">
                      <p className="text-isk-muted">Transfer tercatat: <span className="font-semibold text-isk-text">{fmtDate(selected.bill?.paid_at)}</span></p>
                      <button onClick={() => { setEditingPaidAt(true); setNewPaidAt(selected.bill?.paid_at?.slice(0,10) || ''); }}
                        className="text-isk-teal hover:underline text-xs">Edit Tanggal</button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600 text-center">Pembayaran ditolak</p>
                  <Button size="sm" variant="outline" onClick={handleDelete}
                    className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-full">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Bukti Pembayaran
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentVerification;
